// src/pages/SnagsLanding.jsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import withProjectPermissions from '../components/withProjectPermissions';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, storage } from '../firebase/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import SnagModal from '../components/SnagModal';
import VoiceRecorder from '../components/VoiceRecorder';
import { CheckCircle2, AlertTriangle, ArrowLeft, FileDown, Settings } from 'lucide-react';
import './ActivityView.css';
import { buildSnagsExportPdf, buildSnagsSectionsReportPdf, VOICE_PDF } from '../utils/snagsExportPdf';
import ReportSectionModal from '../components/ReportSectionModal';
import ReportSectionsPanel from '../components/ReportSectionsPanel';
import { useBranding } from '../contexts/BrandingContext';
import { getCompanyLogoUrl } from '../utils/branding';
import PdfExportSettingsModal from '../components/PdfExportSettingsModal';
import { loadSnagsPdfSettings, saveSnagsPdfSettings } from '../utils/pdfExportSettings';

function SnagsLanding({ permissions }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snags, setSnags] = useState([]);
  const [teams, setTeams] = useState([]);
  const [repliesBySnag, setRepliesBySnag] = useState({});
  const [expandedSnags, setExpandedSnags] = useState(new Set());
  const [imageModal, setImageModal] = useState({ open: false, url: null, reply: null, initialIndex: 0 });
  const [pmReplyModal, setPmReplyModal] = useState({ open: false, replyId: null, snagId: null });
  const [pmRepliesByReply, setPmRepliesByReply] = useState({});
  const [closeConfirmModal, setCloseConfirmModal] = useState({ open: false, snagId: null, snagTitle: null });
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [exportSelectionMode, setExportSelectionMode] = useState(false);
  const [exportSelectedIds, setExportSelectedIds] = useState(() => new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [snagsPdfSettings, setSnagsPdfSettings] = useState(() => loadSnagsPdfSettings());
  const [reportSections, setReportSections] = useState([]);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [isSectionExporting, setIsSectionExporting] = useState(false);
  const [pdfExportSettingsOpen, setPdfExportSettingsOpen] = useState(false);
  const { company: brandingCompany } = useBranding();
  const companyLogoUrl = getCompanyLogoUrl(brandingCompany);

  // Get current user
  const auth = getAuth();
  const user = auth.currentUser;

  const isPM = Boolean(project && user && (
    project.createdBy === user.uid ||
    (project.collaborators?.[user.uid]?.status === 'accepted' && project.collaborators?.[user.uid]?.role === 'pm_collaborator')
  ));

  const isReadOnly = project?.status === 'closed' || !permissions?.canEdit;

  // Helper function to calculate smart image size based on count
  const getImageSize = (count) => {
    if (count <= 3) return 180;
    if (count <= 6) return 140;
    return 110;
  };

  useEffect(() => {
    if (!exportSelectionMode) {
      setExportSelectedIds(new Set());
    }
  }, [exportSelectionMode]);

  useEffect(() => {
    saveSnagsPdfSettings(snagsPdfSettings);
  }, [snagsPdfSettings]);

  const formatExportDateTime = (d) => {
    if (!d) return '';
    const dateObj =
      d instanceof Date
        ? d
        : typeof d?.toDate === 'function'
          ? d.toDate()
          : new Date(d);
    if (Number.isNaN(dateObj.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  };

  const prettyLabel = (s) => {
    if (s == null || s === '') return '—';
    return String(s)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getSnagImageUrls = (snag) => {
    if (!snag?.media || !Array.isArray(snag.media)) return [];
    return snag.media
      .filter(
        (m) => m && (m.type === 'image' || (typeof m.type === 'string' && m.type.startsWith('image/')))
      )
      .map((m) => m.url)
      .filter(Boolean);
  };

  const snagHasVoice = (snag) =>
    Boolean(snag?.media?.some((m) => m && m.type === 'voice'));

  const getExportFilterSummary = () => {
    const n = snags.length;
    const s = exportSelectedIds.size;
    if (n === 0 || s === n) return null;
    return `${s} of ${n} item${n !== 1 ? 's' : ''} selected`;
  };

  const snagTimeMs = (s) => {
    const x = s?.createdAt;
    if (!x) return 0;
    if (x instanceof Date) return x.getTime();
    if (typeof x.toDate === 'function') return x.toDate().getTime();
    const d = new Date(x);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const buildSnagItemsForPdf = (settings) => {
    return snags
      .filter((snag) => exportSelectedIds.has(snag.id))
      .sort((a, b) => snagTimeMs(a) - snagTimeMs(b))
      .map((snag) => {
        const dateStr = formatExportDateTime(snag.createdAt);
        const reporter = snag.createdByName || 'Unknown';
        let createdLine = null;
        if (settings.includeReporterName && settings.includeReportedTime) {
          createdLine = `Created by ${reporter} · ${dateStr}`;
        } else if (settings.includeReporterName) {
          createdLine = `Created by ${reporter}`;
        } else if (settings.includeReportedTime) {
          createdLine = `Reported on ${dateStr}`;
        }

        const assignedLine =
          settings.includeAssignedTeams && snag.assignedTeams && snag.assignedTeams.length
            ? `Assigned to: ${snag.assignedTeams
                .map((tid) => teams.find((t) => t.id === tid)?.name)
                .filter(Boolean)
                .join(', ')}`
            : null;

        let resolvedLine = null;
        if (settings.includeResolvedInfo && snag.status === 'resolved') {
          const resolvedAtStr = snag.resolvedAt ? formatExportDateTime(snag.resolvedAt) : null;
          if (settings.includeResolverName && snag.resolvedByName) {
            resolvedLine = resolvedAtStr
              ? `Resolved by ${snag.resolvedByName} on ${resolvedAtStr}`
              : `Resolved by ${snag.resolvedByName}`;
          } else if (resolvedAtStr) {
            resolvedLine = `Resolved on ${resolvedAtStr}`;
          } else {
            resolvedLine = 'Resolved';
          }
        }

        const isVariation = snag.category === 'variation';
        return {
          title: snag.title || 'Untitled',
          category: snag.category || 'snag',
          statusLabel: settings.includeIssueLabels ? `Status: ${prettyLabel(snag.status)}` : '',
          priorityLabel: settings.includeIssueLabels && !isVariation
            ? `Priority: ${snag.priority != null ? String(snag.priority).toUpperCase() : '—'}`
            : '',
          categoryLabel: settings.includeIssueLabels ? `Type: ${prettyLabel(snag.category)}` : '',
          createdLine,
          assignedLine,
          resolvedLine,
          description: settings.includeDescription ? snag.description || '' : '',
          snagVoiceLine: settings.includeSnagVoiceNote && snagHasVoice(snag) ? VOICE_PDF : null,
          imageUrls: settings.includeSnagPhotos ? getSnagImageUrls(snag) : [],
          replies: settings.includeSiteReplies
            ? (repliesBySnag[snag.id] || []).map((reply) => ({
                name: settings.includeSiteReplyAuthorNames ? reply.createdByName || 'Unknown' : '',
                timeLabel: formatExportDateTime(reply.createdAt),
                text: reply.comment || '',
                imageUrls:
                  settings.includeSiteReplyImages && Array.isArray(reply.photoUrls)
                    ? reply.photoUrls.filter(Boolean)
                    : [],
                voiceLine: settings.includeReplyVoiceNotes && reply.voiceNoteUrl ? VOICE_PDF : null,
                pmNotes: settings.includePmNotes
                  ? (pmRepliesByReply[reply.id] || []).map((pm) => ({
                      name: settings.includePmAuthorNames ? pm.createdByName || 'Unknown' : '',
                      timeLabel: formatExportDateTime(pm.createdAt),
                      text: pm.comment || '',
                      imageUrls:
                        settings.includePmNoteImages && Array.isArray(pm.photoUrls)
                          ? pm.photoUrls.filter(Boolean)
                          : [],
                      voiceLine: settings.includePmVoiceNotes && pm.voiceNoteUrl ? VOICE_PDF : null,
                    }))
                  : [],
              }))
            : [],
        };
      });
  };

  const toggleExportSelect = (id) => {
    setExportSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllForExport = () => {
    setExportSelectedIds(new Set(snags.map((s) => s.id)));
  };

  const clearExportSelection = () => {
    setExportSelectedIds(new Set());
  };

  const handleGenerateSnagsPdf = async () => {
    if (reportSections.length > 0) {
      handleExportSectionReport(reportSections);
      return;
    }
    if (exportSelectedIds.size === 0) return;
    setIsExporting(true);
    try {
      const items = buildSnagItemsForPdf(snagsPdfSettings);
      await buildSnagsExportPdf({
        projectName: project?.name || 'Project',
        filterSummary: getExportFilterSummary(),
        items,
        companyLogoUrl: snagsPdfSettings.includeCompanyLogo && companyLogoUrl ? companyLogoUrl : null,
      });
    } catch (err) {
      console.error('Snags PDF export failed:', err);
      alert('Could not generate PDF. If images fail to load, check your connection and try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Sections useEffect and handlers (PM-only)
  useEffect(() => {
    if (!projectId || !isPM) return;
    const q = query(
      collection(db, `projects/${projectId}/report_sections`),
      orderBy('order', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setReportSections(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => s.sectionType === 'snags')
      );
    });
    return () => unsub();
  }, [projectId, isPM]);

  const handleSaveSection = async ({ name, annotation }) => {
    if (isReadOnly) return;
    if (editingSection) {
      await updateDoc(doc(db, `projects/${projectId}/report_sections/${editingSection.id}`), { name, annotation });
    } else {
      await addDoc(collection(db, `projects/${projectId}/report_sections`), {
        sectionType: 'snags',
        name,
        annotation,
        itemIds: Array.from(exportSelectedIds),
        createdAt: serverTimestamp(),
        createdBy: user?.uid,
        order: Date.now(),
      });
      setExportSelectedIds(new Set());
    }
    setSectionModalOpen(false);
    setEditingSection(null);
  };

  const handleDeleteSection = async (section) => {
    if (isReadOnly) return;
    await deleteDoc(doc(db, `projects/${projectId}/report_sections/${section.id}`));
  };

  const handleExportSectionReport = async (checkedSections) => {
    if (!checkedSections.length) return;
    setIsSectionExporting(true);
    try {
      const settings = snagsPdfSettings;
      const sections = checkedSections.map(section => {
        const items = (section.itemIds || [])
          .map(id => snags.find(s => s.id === id))
          .filter(Boolean)
          .sort((a, b) => snagTimeMs(a) - snagTimeMs(b))
          .map(snag => {
            const dateStr = formatExportDateTime(snag.createdAt);
            const reporter = snag.createdByName || 'Unknown';
            let createdLine = null;
            if (settings.includeReporterName && settings.includeReportedTime) createdLine = `Created by ${reporter} · ${dateStr}`;
            else if (settings.includeReporterName) createdLine = `Created by ${reporter}`;
            else if (settings.includeReportedTime) createdLine = `Reported on ${dateStr}`;
            const assignedLine = settings.includeAssignedTeams && snag.assignedTeams?.length
              ? `Assigned to: ${snag.assignedTeams.map(tid => teams.find(t => t.id === tid)?.name).filter(Boolean).join(', ')}`
              : null;
            let resolvedLine = null;
            if (settings.includeResolvedInfo && snag.status === 'resolved') {
              const resolvedAtStr = snag.resolvedAt ? formatExportDateTime(snag.resolvedAt) : null;
              if (settings.includeResolverName && snag.resolvedByName) resolvedLine = resolvedAtStr ? `Resolved by ${snag.resolvedByName} on ${resolvedAtStr}` : `Resolved by ${snag.resolvedByName}`;
              else if (resolvedAtStr) resolvedLine = `Resolved on ${resolvedAtStr}`;
              else resolvedLine = 'Resolved';
            }
            const isVariation = snag.category === 'variation';
            return {
              title: snag.title || 'Untitled',
              category: snag.category || 'snag',
              statusLabel: settings.includeIssueLabels ? `Status: ${prettyLabel(snag.status)}` : '',
              priorityLabel: settings.includeIssueLabels && !isVariation ? `Priority: ${snag.priority != null ? String(snag.priority).toUpperCase() : '—'}` : '',
              categoryLabel: settings.includeIssueLabels ? `Type: ${prettyLabel(snag.category)}` : '',
              createdLine, assignedLine, resolvedLine,
              description: settings.includeDescription ? snag.description || '' : '',
              snagVoiceLine: settings.includeSnagVoiceNote && snagHasVoice(snag) ? VOICE_PDF : null,
              imageUrls: settings.includeSnagPhotos ? getSnagImageUrls(snag) : [],
              replies: settings.includeSiteReplies
                ? (repliesBySnag[snag.id] || []).map(reply => ({
                    name: settings.includeSiteReplyAuthorNames ? reply.createdByName || 'Unknown' : '',
                    timeLabel: formatExportDateTime(reply.createdAt),
                    text: reply.comment || '',
                    imageUrls: settings.includeSiteReplyImages && Array.isArray(reply.photoUrls) ? reply.photoUrls.filter(Boolean) : [],
                    voiceLine: settings.includeReplyVoiceNotes && reply.voiceNoteUrl ? VOICE_PDF : null,
                    pmNotes: settings.includePmNotes
                      ? (pmRepliesByReply[reply.id] || []).map(pm => ({
                          name: settings.includePmAuthorNames ? pm.createdByName || 'Unknown' : '',
                          timeLabel: formatExportDateTime(pm.createdAt),
                          text: pm.comment || '',
                          imageUrls: settings.includePmNoteImages && Array.isArray(pm.photoUrls) ? pm.photoUrls.filter(Boolean) : [],
                          voiceLine: settings.includePmVoiceNotes && pm.voiceNoteUrl ? VOICE_PDF : null,
                        }))
                      : [],
                  }))
                : [],
            };
          });
        return { name: section.name, annotation: section.annotation || '', items };
      });
      await buildSnagsSectionsReportPdf({
        projectName: project?.name || 'Project',
        sections,
        companyLogoUrl: settings.includeCompanyLogo && companyLogoUrl ? companyLogoUrl : null,
      });
    } catch (err) {
      console.error('Snag section report export failed:', err);
      alert('Could not generate PDF. Check your connection and try again.');
    } finally {
      setIsSectionExporting(false);
    }
  };

  // Render media function (photos and voice notes)
  const renderMedia = (mediaUrls, mediaType = 'photo', reply = null, isReply = false) => {
    if (!mediaUrls || mediaUrls.length === 0) return null;

    if (mediaType === 'voice') {
      // Voice notes get their own simple layout without grid
      return (
        <div style={{ marginTop: 16 }}>
          <h6 style={{ fontSize: 12, fontWeight: 500, color: 'var(--cc-text-primary)', margin: '0 0 12px 0' }}>
            Voice Note:
          </h6>
          <VoicePlayer 
            voiceUrl={mediaUrls[0]} 
            replyId={reply?.id} 
            userId={reply?.createdBy}
          />
        </div>
      );
    }

    // Photos use flex-wrap layout with smart sizing
    const imageSize = getImageSize(mediaUrls.length);
    
    return (
      <div style={{ marginTop: isReply ? 0 : 16 }}>
        {!isReply && (
          <h6 style={{ fontSize: 12, fontWeight: 500, color: 'var(--cc-text-primary)', margin: '0 0 12px 0' }}>
            Photos:
          </h6>
        )}
        <div style={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          width: '100%'
        }}>
          {mediaUrls.map((url, index) => (
            <img
              key={index}
              src={url}
              alt={`Attachment ${index + 1}`}
              style={{
                width: imageSize,
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 8,
                border: '1px solid var(--branding-app-surface-panel-border)',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                background: 'var(--branding-app-surface-panel)',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
              onClick={() => setImageModal({ open: true, url: mediaUrls.join(','), reply: reply, initialIndex: index })}
            />
          ))}
        </div>
      </div>
    );
  };

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          setProject({ id: projectDoc.id, ...projectDoc.data() });
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };
    fetchProject();
  }, [projectId]);

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      if (!projectId) return;
      try {
        const teamsSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
        const teamsData = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeams(teamsData);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };
    fetchTeams();
  }, [projectId]);

  // Fetch existing snags
  useEffect(() => {
    const fetchSnags = async () => {
      if (!projectId) return;
      try {
        const snagsQuery = query(
          collection(db, `projects/${projectId}/snags`),
          orderBy('createdAt', 'desc')
        );
        const snagsSnapshot = await getDocs(snagsQuery);
        const snagsData = snagsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || null,
            updatedAt: data.updatedAt?.toDate?.() || null,
            resolvedAt: data.resolvedAt?.toDate?.() || null
          };
        });
        setSnags(snagsData);
      } catch (error) {
        console.error('Error fetching snags:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSnags();
  }, [projectId]);

  // Fetch replies for each snag
  useEffect(() => {
    const fetchReplies = async () => {
      if (!projectId || snags.length === 0) return;
      
      try {
        const repliesObj = {};
        for (const snag of snags) {
          const repliesQuery = query(
            collection(db, `projects/${projectId}/snags/${snag.id}/replies`),
            orderBy('createdAt', 'asc')
          );
          const repliesSnapshot = await getDocs(repliesQuery);
          const repliesData = repliesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate()
          }));
          repliesObj[snag.id] = repliesData;
        }
        setRepliesBySnag(repliesObj);
      } catch (error) {
        console.error('Error fetching replies:', error);
      }
    };
    fetchReplies();
  }, [projectId, snags]);

  // Fetch PM replies for each reply
  useEffect(() => {
    const fetchPmReplies = async () => {
      if (!projectId || Object.keys(repliesBySnag).length === 0) return;
      
      try {
        const pmRepliesObj = {};
        for (const [snagId, replies] of Object.entries(repliesBySnag)) {
          for (const reply of replies) {
            const pmRepliesQuery = query(
              collection(db, `projects/${projectId}/snags/${snagId}/replies/${reply.id}/pmReplies`),
              orderBy('createdAt', 'asc')
            );
            const pmRepliesSnapshot = await getDocs(pmRepliesQuery);
            const pmRepliesData = pmRepliesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate()
            }));
            pmRepliesObj[reply.id] = pmRepliesData;
          }
        }
        setPmRepliesByReply(pmRepliesObj);
      } catch (error) {
        console.error('Error fetching PM replies:', error);
      }
    };
    fetchPmReplies();
  }, [projectId, repliesBySnag]);

  const handleCreateSnag = async (snagData) => {
    if (isReadOnly) return;
    try {
      const newSnagData = {
        ...snagData,
        projectId,
        createdBy: user?.uid || 'anonymous',
        createdByName: user?.displayName || user?.email || 'Unknown User',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add to Firebase
      const docRef = await addDoc(
        collection(db, `projects/${projectId}/snags`),
        newSnagData
      );

      // Add to local state for immediate UI update
      const localSnagData = {
        id: docRef.id,
        ...newSnagData,
        createdAt: new Date() // Use current date for immediate display
      };
      setSnags(prev => [localSnagData, ...prev]);

      console.log('Snag created with ID:', docRef.id);
    } catch (error) {
      console.error('Error creating snag:', error);
      throw error; // Let the modal handle the error
    }
  };

  const handleCloseSnag = async (snagId) => {
    if (isReadOnly) return;
    try {
      // Update in Firebase
      const snagRef = doc(db, `projects/${projectId}/snags`, snagId);
      await updateDoc(snagRef, {
        status: 'resolved',
        updatedAt: serverTimestamp(),
        resolvedBy: user?.uid || 'anonymous',
        resolvedByName: user?.displayName || user?.email || 'Unknown User',
        resolvedAt: serverTimestamp()
      });

      // Update local state
      setSnags(prev => prev.map(snag => 
        snag.id === snagId 
          ? { 
              ...snag, 
              status: 'resolved',
              updatedAt: new Date(),
              resolvedBy: user?.uid || 'anonymous',
              resolvedByName: user?.displayName || user?.email || 'Unknown User',
              resolvedAt: new Date()
            }
          : snag
      ));

      console.log('Snag closed successfully');
    } catch (error) {
      console.error('Error closing snag:', error);
      alert('Failed to close snag. Please try again.');
    }
  };

  const toggleReplies = (snagId) => {
    setExpandedSnags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(snagId)) {
        newSet.delete(snagId);
      } else {
        newSet.add(snagId);
      }
      return newSet;
    });
  };

  const VoicePlayer = ({ voiceUrl, replyId, userId }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [audio, setAudio] = useState(null);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    useEffect(() => {
      if (voiceUrl) {
        const audioElement = new Audio(voiceUrl);
        audioElement.addEventListener('loadedmetadata', () => {
          setDuration(audioElement.duration);
        });
        audioElement.addEventListener('timeupdate', () => {
          setCurrentTime(audioElement.currentTime);
        });
        audioElement.addEventListener('ended', () => {
          setIsPlaying(false);
          setCurrentTime(0);
        });
        setAudio(audioElement);
      }
    }, [voiceUrl]);

    const togglePlay = () => {
      if (!audio) return;
      
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
      }
    };

    const formatTime = (time) => {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleSeek = (e) => {
      if (!audio) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const seekTime = (clickX / width) * duration;
      audio.currentTime = seekTime;
      setCurrentTime(seekTime);
    };

    if (!voiceUrl) return null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'var(--branding-app-surface-panel)',
        borderRadius: 8,
        border: '1px solid var(--branding-app-surface-panel-border)',
        marginTop: 12,
        minWidth: 300
      }}>
        <button
          onClick={togglePlay}
          style={{
            background: 'var(--branding-app-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 16,
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'var(--branding-app-primary-hover)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'var(--branding-app-primary)';
          }}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <div style={{ flex: 1, minWidth: 250 }}>
          <div
            onClick={handleSeek}
            style={{
              width: '100%',
              height: 6,
              background: 'var(--cc-active-bg)',
              borderRadius: 3,
              cursor: 'pointer',
              position: 'relative',
              marginBottom: 6
            }}
          >
            <div
              style={{
                width: `${(currentTime / duration) * 100}%`,
                height: '100%',
                background: 'var(--branding-app-primary)',
                borderRadius: 3,
                transition: 'width 0.1s'
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13,
            color: 'var(--cc-text-muted)',
            fontWeight: 500
          }}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    );
  };

  const CloseConfirmModal = ({ open, onClose, snagId, snagTitle, onConfirm }) => {
    if (!open) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'var(--cc-card-bg)',
          padding: '24px',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '450px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            <AlertTriangle 
              size={32} 
              color="#f59e0b" 
              style={{ marginRight: '12px' }}
            />
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cc-text-primary)', margin: 0 }}>
              Close Snag
            </h3>
          </div>
          
          <p style={{
            fontSize: '14px',
            color: 'var(--cc-text-muted)',
            lineHeight: 1.5,
            marginBottom: '24px'
          }}>
            Are you sure you want to mark "<strong>{snagTitle}</strong>" as resolved? 
            This action cannot be undone.
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <button
              onClick={onClose}
              style={{
                background: 'var(--cc-cancel-bg)',
                color: 'var(--cc-text-primary)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '500',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
                          <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                style={{
                  background: 'transparent',
                  border: '2px solid #10b981',
                  color: '#000000',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#10b981';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#000000';
                }}
              >
                <CheckCircle2 
                  size={16} 
                  style={{
                    color: 'inherit',
                    transition: 'none'
                  }}
                />
                Confirm Close
              </button>
          </div>
        </div>
      </div>
    );
  };

  const PmReplyModal = ({ open, onClose, replyId, snagId }) => {
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [voiceNoteFile, setVoiceNoteFile] = useState(null);

    const handleFileSelect = (e) => {
      const files = Array.from(e.target.files);
      const validFiles = files.filter(file => {
        return file.type.startsWith('image/');
      });
      setAttachedFiles(prev => [...prev, ...validFiles]);
    };

    const removeFile = (index) => {
      setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleClose = () => {
      if (!submitting) {
        setComment('');
        setAttachedFiles([]);
        setVoiceNoteFile(null);
        onClose();
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (isReadOnly || !comment.trim() || !replyId || !snagId) return;

      setSubmitting(true);
      try {
        let photoUrls = [];
        let voiceNoteUrl = null;

        // Upload images if any
        if (attachedFiles.length > 0) {
          setUploadingFiles(true);
          const timestamp = Date.now();
          const imageUploadPromises = attachedFiles.map(async (file, index) => {
            const storageRef = ref(storage, `projects/${projectId}/snags/${snagId}/replies/${replyId}/pmReplies/media/${timestamp}_${index}.jpg`);
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
          });
          photoUrls = await Promise.all(imageUploadPromises);
          setUploadingFiles(false);
        }

        // Upload voice note if present
        if (voiceNoteFile) {
          setUploadingFiles(true);
          const timestamp = Date.now();
          const fileExtension = voiceNoteFile.name.split('.').pop() || 'mp4';
          const voiceStorageRef = ref(storage, `projects/${projectId}/snags/${snagId}/replies/${replyId}/pmReplies/media/${timestamp}_voice.${fileExtension}`);
          await uploadBytes(voiceStorageRef, voiceNoteFile);
          voiceNoteUrl = await getDownloadURL(voiceStorageRef);
          setUploadingFiles(false);
        }

        const pmReplyData = {
          comment: comment.trim(),
          createdBy: user?.uid || 'anonymous',
          createdByName: user?.displayName || user?.email || 'Unknown User',
          createdAt: serverTimestamp(),
          isPmReply: true,
          parentReplyId: replyId,
          photoUrls: photoUrls.length > 0 ? photoUrls : undefined,
          voiceNoteUrl: voiceNoteUrl || undefined
        };

        await addDoc(
          collection(db, `projects/${projectId}/snags/${snagId}/replies/${replyId}/pmReplies`),
          pmReplyData
        );

        setComment('');
        setAttachedFiles([]);
        setVoiceNoteFile(null);
        onClose();
        console.log('PM reply added successfully');
      } catch (error) {
        console.error('Error adding PM reply:', error);
        alert('Failed to add PM reply. Please try again.');
      } finally {
        setSubmitting(false);
        setUploadingFiles(false);
      }
    };

    if (!open) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'var(--cc-card-bg)',
          padding: '24px',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cc-text-primary)', margin: 0 }}>
              Add Comment
            </h3>
            <button
              onClick={handleClose}
              disabled={submitting}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '20px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                color: 'var(--cc-text-muted)'
              }}
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            {/* Comment Input - Fixed height like iOS */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14
              }}>
                Comment
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'none',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: submitting ? '#f9fafb' : '#fff'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--branding-app-primary)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#cbd5e1';
                }}
              />
            </div>

            {/* Image Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14
              }}>
                Attach Photos
              </label>
              <div style={{
                border: '2px dashed #d1d5db',
                borderRadius: 6,
                padding: 20,
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={submitting}
                  style={{ display: 'none' }}
                  id="pm-reply-file-input"
                />
                <label
                  htmlFor="pm-reply-file-input"
                  style={{
                    display: 'inline-block',
                    background: submitting ? '#cbd5e1' : 'var(--branding-app-primary)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Choose Files
                </label>
                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: 'var(--cc-text-muted)' }}>
                  Images only. Multiple files allowed.
                </p>
              </div>

              {/* Image Preview */}
              {attachedFiles.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {attachedFiles.map((file, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 6,
                            border: '1px solid var(--branding-app-surface-panel-border)'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          disabled={submitting}
                          style={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            fontSize: 16,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Voice Note */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14
              }}>
                Voice Note (Optional)
              </label>
              <VoiceRecorder 
                onVoiceRecorded={(file) => setVoiceNoteFile(file)}
                disabled={submitting}
              />
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                style={{
                  background: 'transparent',
                  border: '1px solid #cbd5e1',
                  color: 'var(--cc-text-muted)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !comment.trim()}
                style={{
                  background: submitting || !comment.trim() ? '#cbd5e1' : 'var(--branding-app-primary)',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: submitting || !comment.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!submitting && comment.trim()) {
                    e.target.style.backgroundColor = 'var(--branding-app-primary-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting && comment.trim()) {
                    e.target.style.backgroundColor = 'var(--branding-app-primary)';
                  }
                }}
              >
                {submitting ? (uploadingFiles ? 'Uploading...' : 'Adding...') : 'Add Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const ImageModal = ({ open, onClose, imageUrl, reply, initialIndex = 0 }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialIndex);
  
  // Split comma-separated image URLs
  const imageUrls = imageUrl ? imageUrl.split(',').map(url => url.trim()) : [];
  
  // Reset to initial index when modal opens or initialIndex changes
  useEffect(() => {
    if (open) {
      setCurrentImageIndex(initialIndex);
    }
  }, [open, initialIndex]);
  
  const handleDownload = () => {
    if (imageUrls.length > 0) {
      const link = document.createElement('a');
      link.href = imageUrls[currentImageIndex];
      link.download = `snag_reply_${reply?.id || 'image'}_${currentImageIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % imageUrls.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  };

  if (!open || imageUrls.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.35)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 14,
        padding: 0,
        minWidth: 340,
        maxWidth: '90vw',
        maxHeight: '90vh',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 28px 10px 28px',
          borderBottom: '1px solid var(--branding-app-surface)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={handleDownload}
              style={{
                background: 'var(--branding-app-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 14,
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--branding-app-primary-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--branding-app-primary)';
              }}
            >
              Download
            </button>
            <span style={{ fontWeight: 600, fontSize: 18, color: 'var(--cc-text-primary)' }}>
              {imageUrls.length > 1 ? `${currentImageIndex + 1} of ${imageUrls.length}` : ''}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--cc-cancel-bg)',
              color: 'var(--cc-text-primary)',
              border: 'none',
              borderRadius: 6,
              padding: '4px 14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            Close
          </button>
        </div>
        <div style={{
          padding: 24,
          overflow: 'auto',
          maxHeight: '70vh',
          minHeight: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          {/* Navigation arrows for multiple images */}
          {imageUrls.length > 1 && (
            <>
              <button
                onClick={prevImage}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  zIndex: 10
                }}
              >
                ‹
              </button>
              <button
                onClick={nextImage}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  zIndex: 10
                }}
              >
                ›
              </button>
            </>
          )}
          <img
            src={imageUrls[currentImageIndex]}
            alt="Reply"
            style={{
              maxWidth: '90vw',
              maxHeight: '70vh',
              width: 'auto',
              height: 'auto',
              borderRadius: 8,
              border: '1px solid var(--branding-app-surface-panel-border)',
              background: 'var(--branding-app-surface-panel)',
              objectFit: 'contain'
            }}
            onLoad={(e) => {
              // Optimize display for phone photos
              const img = e.target;
              const aspectRatio = img.naturalWidth / img.naturalHeight;
              
              if (aspectRatio < 0.8) {
                // Very tall portrait (typical phone selfie)
                img.style.maxHeight = '80vh';
                img.style.width = 'auto';
              } else if (aspectRatio > 1.5) {
                // Very wide landscape
                img.style.maxWidth = '85vw';
                img.style.height = 'auto';
              }
              // Default behavior for normal aspect ratios
            }}
          />
        </div>
        {/* Image indicators for multiple images */}
        {imageUrls.length > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            padding: '16px 24px',
            borderTop: '1px solid var(--branding-app-surface)'
          }}>
            {imageUrls.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: index === currentImageIndex ? 'var(--branding-app-primary)' : '#cbd5e1',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <h2>Loading project...</h2>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: 40 }}>
        <h2>Project not found</h2>
      </div>
    );
  }

  return (
    <div className="snags-container" style={{ 
      padding: '20px', 
      backgroundColor: 'var(--branding-app-surface)', 
      minHeight: '100vh' 
    }}>
      {/* Back Button */}
      <div style={{ marginBottom: 24, paddingLeft: 20 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = '#6b7280';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (min-width: 768px) {
          .snags-container {
            padding: 40px !important;
          }
          .header-section {
            flex-direction: row !important;
            align-items: flex-start !important;
          }
          .header-content {
            margin-bottom: 32px !important;
          }
        }
        @media (max-width: 767px) {
          .snags-container {
            padding: 20px !important;
          }
          .header-section {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 16px !important;
          }
          .header-content {
            margin-bottom: 16px !important;
          }
        }
      `}</style>
      
      {/* Header Section */}
      <div className="header-section" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: 32 
      }}>
        <div className="header-content">
          <h1 style={{ fontSize: 'clamp(20px, 2.2vw, 22px)', fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 24 }}>
            {project.name}
          </h1>
          {project.projectCode && (
            <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 12, lineHeight: 1.4 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 16, lineHeight: 1.4 }}>
            <strong>Address:</strong> {project.address}
          </p>
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 24, lineHeight: 1.4 }}>
            <strong>Postcode:</strong> {project.postcode}
          </p>
        </div>
      </div>

      {/* Main Content Panel */}
      <div 
        className="main-content-panel"
        style={{
          background: 'var(--cc-card-bg)',
          borderRadius: 16,
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          minHeight: 300,
          maxWidth: 1100,
          margin: '0 auto',
          width: '95%'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>
            Snags and variations
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => setExportSelectionMode((v) => !v)}
              disabled={!project}
              aria-pressed={exportSelectionMode}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                padding: 0,
                background: exportSelectionMode
                  ? 'var(--branding-app-primary)'
                  : 'var(--branding-app-surface-panel)',
                color: exportSelectionMode
                  ? '#fff'
                  : '#475569',
                border: `1px solid ${exportSelectionMode ? 'var(--branding-app-primary)' : '#e2e8f0'}`,
                borderRadius: 8,
                cursor: !project ? 'not-allowed' : 'pointer',
                opacity: !project ? 0.5 : 1,
                boxSizing: 'border-box',
              }}
              title={
                exportSelectionMode
                  ? 'Cancel export selection'
                  : 'Export to PDF (select snags)'
              }
            >
              <FileDown size={20} />
            </button>
            <button
              type="button"
              onClick={() => {
                if (isReadOnly) return;
                setIsModalOpen(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 40,
                height: 40,
                background: isReadOnly ? '#cbd5e1' : 'var(--branding-app-primary)',
                color: isReadOnly ? '#64748b' : '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontWeight: 500,
                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                fontSize: 18,
                lineHeight: 1,
                opacity: isReadOnly ? 0.6 : 1,
              }}
              disabled={isReadOnly}
              title={project?.status === 'closed' ? 'Project is closed - no new snags allowed' : (isReadOnly ? 'You do not have permission to add snags' : 'Add Snag')}
            >
              +
            </button>
          </div>
        </div>

        {/* PM report sections panel */}
        {isPM && (
          <ReportSectionsPanel
            sections={reportSections}
            onEdit={(section) => { setEditingSection(section); setSectionModalOpen(true); }}
            onDelete={handleDeleteSection}
            onExportSections={handleExportSectionReport}
            isExporting={isSectionExporting}
          />
        )}

        {exportSelectionMode && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 12,
              marginBottom: 20,
              padding: '12px 16px',
              background: 'var(--branding-app-surface-panel)',
              borderRadius: 8,
              border: '1px solid var(--branding-app-surface-panel-border)',
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--cc-text-primary)', fontSize: 15 }}>PDF export</span>
            <button
              type="button"
              onClick={selectAllForExport}
              disabled={snags.length === 0}
              style={{
                background: 'var(--cc-cancel-bg)',
                color: snags.length === 0 ? '#94a3b8' : '#1e293b',
                border: 'none',
                borderRadius: 6,
                padding: '6px 12px',
                fontWeight: 500,
                fontSize: 14,
                cursor: snags.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearExportSelection}
              style={{
                background: 'var(--cc-cancel-bg)',
                color: 'var(--cc-text-primary)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 12px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
            {isPM && exportSelectedIds.size > 0 && (
              <button
                type="button"
                onClick={() => { setEditingSection(null); setSectionModalOpen(true); }}
                style={{
                  background: 'var(--branding-app-primary)',
                  color: 'var(--branding-app-primary-fg)',
                  border: 'none', borderRadius: 6, padding: '6px 14px',
                  fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
                title="Save selected snags as a named report section"
              >
                Save as section
              </button>
            )}
            <button
              type="button"
              onClick={() => setPdfExportSettingsOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                padding: 0,
                background: 'var(--cc-card-bg)',
                color: 'var(--cc-text-secondary)',
                border: '1px solid var(--branding-app-surface-panel-border)',
                borderRadius: 8,
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
              title="PDF export options"
              aria-label="PDF export options"
            >
              <Settings size={20} />
            </button>
            <button
              type="button"
              onClick={handleGenerateSnagsPdf}
              disabled={
                (reportSections.length === 0 && exportSelectedIds.size === 0) ||
                isExporting ||
                isSectionExporting
              }
              style={{
                background:
                  (reportSections.length === 0 && exportSelectedIds.size === 0) || isExporting || isSectionExporting
                    ? '#cbd5e1'
                    : 'var(--branding-app-primary)',
                color:
                  (reportSections.length === 0 && exportSelectedIds.size === 0) || isExporting || isSectionExporting
                    ? '#64748b'
                    : '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                fontWeight: 600,
                fontSize: 14,
                cursor:
                  (reportSections.length === 0 && exportSelectedIds.size === 0) || isExporting || isSectionExporting
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {isExporting || isSectionExporting ? 'Generating…' : 'Generate PDF'}
            </button>
            <span style={{ color: 'var(--cc-text-muted)', fontSize: 14 }}>
              {exportSelectedIds.size} selected
            </span>
          </div>
        )}

        {/* Closed project notice */}
        {project?.status === 'closed' && (
          <div style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: 8,
            padding: 16,
            marginBottom: 24,
            textAlign: 'center'
          }}>
            <p style={{
              margin: 0,
              color: '#92400e',
              fontSize: 14,
              fontWeight: 500
            }}>
              This project is closed and read-only. You can view all snags but cannot add new ones.
            </p>
          </div>
        )}

        {/* Snags List or Placeholder */}
        {snags.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            color: 'var(--cc-text-muted)',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 500, color: 'var(--cc-text-secondary)', marginBottom: 8 }}>
              No snags reported yet
            </h3>
            <p style={{ fontSize: 14, color: 'var(--cc-text-muted)', maxWidth: 400 }}>
              This is where project snags and variations will be tracked and managed. 
              Use the "+" button to report issues that need attention.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {snags.map(snag => {
              const replies = repliesBySnag[snag.id] || [];
              const hasReplies = replies.length > 0;
              const isExpanded = expandedSnags.has(snag.id);

              return (
                <div key={snag.id} style={{
                  background: 'var(--branding-app-surface-panel)',
                  borderRadius: 8,
                  padding: 16,
                  border: '1px solid var(--branding-app-surface-panel-border)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                      {exportSelectionMode && (
                        <input
                          type="checkbox"
                          checked={exportSelectedIds.has(snag.id)}
                          onChange={() => toggleExportSelect(snag.id)}
                          aria-label="Select snag for PDF export"
                          style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                        />
                      )}
                      <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>
                        {snag.title}
                      </h4>
                      <span style={{
                        background: snag.status === 'open' ? '#ef4444' : 
                                  snag.status === 'in_progress' ? '#f97316' :
                                  snag.status === 'resolved' ? '#10b981' : '#6b7280',
                        color: '#fff',
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}>
                        {snag.status?.replace('_', ' ') || 'unknown'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{
                        background: snag.priority === 'urgent' ? '#ef4444' : 
                                  snag.priority === 'high' ? '#f97316' :
                                  snag.priority === 'medium' ? '#eab308' : '#10b981',
                        color: '#fff',
                        fontSize: 12,
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontWeight: 500
                      }}>
                        {snag.priority.toUpperCase()}
                      </span>
                      <span style={{
                        background: 'var(--cc-active-bg)',
                        color: 'var(--cc-text-secondary)',
                        fontSize: 12,
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontWeight: 500
                      }}>
                        {snag.category}
                      </span>
                      {!isReadOnly && (snag.status === 'open' || snag.status === 'Open' || snag.status === 'OPEN' ||
                        snag.status === 'in_progress' || snag.status === 'In Progress' || snag.status === 'IN_PROGRESS') && (
                        <button
                          onClick={() => setCloseConfirmModal({ open: true, snagId: snag.id, snagTitle: snag.title })}
                          style={{
                            background: '#6b7280',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '8px 16px',
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#4b5563';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#6b7280';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                          }}
                          title="Mark as resolved"
                        >
                          Close
                        </button>
                      )}
                    </div>
                  </div>
                  {snag.description && (
                    <p style={{ fontSize: 14, color: 'var(--cc-text-muted)', margin: '8px 0' }}>
                      {snag.description}
                    </p>
                  )}
                  
                  {/* Assigned Teams */}
                  {snag.assignedTeams && snag.assignedTeams.length > 0 && (
                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                      <h5 style={{ fontSize: 13, fontWeight: 500, color: 'var(--cc-text-primary)', margin: '0 0 4px 0' }}>
                        Assigned to:
                      </h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {snag.assignedTeams.map((teamId, index) => {
                          const team = teams.find(t => t.id === teamId);
                          return team ? (
                            <span key={index} style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              background: 'var(--branding-app-surface)',
                              border: '1px solid var(--branding-app-surface-panel-border)',
                              borderRadius: 12,
                              padding: '2px 8px',
                              fontSize: 12,
                              fontWeight: 500,
                              color: 'var(--cc-text-primary)'
                            }}>
                              <span style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: team.color
                              }} />
                              {team.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Attached Media */}
                  {snag.media && snag.media.length > 0 && (
                    <div style={{ marginTop: 12, marginBottom: 8 }}>
                      <h5 style={{ fontSize: 13, fontWeight: 500, color: 'var(--cc-text-primary)', margin: '0 0 8px 0' }}>
                        Attachments:
                      </h5>
                      
                      {/* Voice Notes Section */}
                      {snag.media.filter(m => m.type === 'voice').length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          {snag.media.filter(m => m.type === 'voice').map((media, index) => (
                            <VoicePlayer 
                              key={`voice-${index}`}
                              voiceUrl={media.url} 
                              replyId={`snag-${snag.id}`} 
                              userId={snag.createdBy}
                            />
                          ))}
                        </div>
                      )}
                      
                      {/* Images - ActivityLanding-style grid layout */}
                      {(() => {
                        const allImages = snag.media.filter(m => m.type.startsWith('image/') || m.type === 'image');
                        const allImageUrls = allImages.map(m => m.url);
                        
                        return allImageUrls.length > 0 && (
                          <div style={{
                            marginBottom: 16,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12
                          }}>
                            {allImageUrls.length === 1 ? (
                              // Single image - centered with max-width
                              <div
                                className="activity-image-single-container"
                                onClick={() => setImageModal({ 
                                  open: true, 
                                  url: allImageUrls.join(','), 
                                  reply: null, 
                                  initialIndex: 0 
                                })}
                              >
                                {(() => {
                                  const imageKey = `snag-${snag.id}-single`;
                                  const isLoaded = loadedImages.has(imageKey);
                                  return (
                                    <div style={{ position: 'relative', width: '100%' }}>
                                      {!isLoaded && (
                                        <div className="activity-image-skeleton activity-image-skeleton-single" />
                                      )}
                                      <img
                                        src={allImageUrls[0]}
                                        alt="Snag"
                                        className={`activity-image-single ${isLoaded ? 'loaded' : ''}`}
                                        loading="lazy"
                                        onLoad={() => setLoadedImages(prev => new Set(prev).add(imageKey))}
                                      />
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : (
                              // Multiple images - grid layout
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: 12,
                                maxWidth: 600,
                                marginLeft: 'auto',
                                marginRight: 'auto'
                              }}>
                                {allImageUrls.map((url, index) => {
                                  const imageKey = `snag-${snag.id}-grid-${index}`;
                                  const isLoaded = loadedImages.has(imageKey);
                                  return (
                                    <div
                                      key={index}
                                      className="activity-image-grid-container"
                                      style={{ position: 'relative' }}
                                      onClick={() => setImageModal({ 
                                        open: true, 
                                        url: allImageUrls.join(','), 
                                        reply: null, 
                                        initialIndex: index 
                                      })}
                                    >
                                      {!isLoaded && (
                                        <div className="activity-image-skeleton activity-image-skeleton-grid" />
                                      )}
                                      <img
                                        src={url}
                                        alt={`Snag ${index + 1}`}
                                        className={`activity-image-grid ${isLoaded ? 'loaded' : ''}`}
                                        loading="lazy"
                                        onLoad={() => setLoadedImages(prev => new Set(prev).add(imageKey))}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {allImageUrls.length > 1 && (
                              <div style={{
                                textAlign: 'center',
                                fontSize: 14,
                                color: 'var(--cc-text-muted)',
                                marginTop: 8
                              }}>
                                {allImageUrls.length} photos
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Creator and timestamp info */}
                  <div style={{ 
                    fontSize: 12, 
                    color: 'var(--branding-app-surface-panel-hint)', 
                    marginTop: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>Created by: {snag.createdByName}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {hasReplies && (
                        <span style={{ fontSize: 11, color: 'var(--cc-text-muted)' }}>
                          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                        </span>
                      )}
                      {snag.createdAt && (() => {
                        // Ensure createdAt is a Date object (handle Date, Timestamp, or other formats)
                        let createdDate;
                        if (snag.createdAt instanceof Date) {
                          createdDate = snag.createdAt;
                        } else if (snag.createdAt?.toDate && typeof snag.createdAt.toDate === 'function') {
                          // Firestore Timestamp
                          createdDate = snag.createdAt.toDate();
                        } else {
                          // Fallback: try to convert to Date
                          createdDate = new Date(snag.createdAt);
                        }
                        return (
                          <span>
                            {createdDate.toLocaleDateString('en-GB')} at {createdDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Resolved info */}
                  {snag.status === 'resolved' && snag.resolvedByName && (
                    <div style={{ 
                      fontSize: 12, 
                      color: '#10b981', 
                      marginTop: 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <span>✓</span>
                      <span>Resolved by {snag.resolvedByName}</span>
                      {snag.resolvedAt && (() => {
                        // Ensure resolvedAt is a Date object (handle Date, Timestamp, or other formats)
                        let resolvedDate;
                        if (snag.resolvedAt instanceof Date) {
                          resolvedDate = snag.resolvedAt;
                        } else if (snag.resolvedAt?.toDate && typeof snag.resolvedAt.toDate === 'function') {
                          // Firestore Timestamp
                          resolvedDate = snag.resolvedAt.toDate();
                        } else {
                          // Fallback: try to convert to Date
                          resolvedDate = new Date(snag.resolvedAt);
                        }
                        return (
                          <span>
                            on {resolvedDate.toLocaleDateString('en-GB')} at {resolvedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        );
                      })()}
                    </div>
                  )}

                  {/* Replies Section */}
                  {hasReplies && (
                    <div style={{ marginTop: 12 }}>
                      <button
                        onClick={() => toggleReplies(snag.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--branding-app-primary)',
                          fontSize: 15,
                          fontWeight: 500,
                          cursor: 'pointer',
                          padding: '4px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        {isExpanded ? '▼' : '▶'} Show replies ({replies.length})
                      </button>
                      
                      {isExpanded && (
                        <div style={{ 
                          marginTop: 8, 
                          paddingLeft: 16,
                          borderLeft: '2px solid #e2e8f0'
                        }}>
                          {replies.map((reply, index) => (
                            <div key={reply.id} style={{
                              background: 'var(--cc-card-bg)',
                              borderRadius: 6,
                              padding: 12,
                              marginBottom: 8,
                              border: '1px solid var(--branding-app-surface-panel-border)',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                              <div style={{ 
                                fontSize: 12, 
                                color: 'var(--branding-app-surface-panel-hint)', 
                                marginBottom: 6,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <span>{reply.createdByName}</span>
                                {reply.createdAt && (() => {
                                  // Ensure createdAt is a Date object (handle Date, Timestamp, or other formats)
                                  let createdDate;
                                  if (reply.createdAt instanceof Date) {
                                    createdDate = reply.createdAt;
                                  } else if (reply.createdAt?.toDate && typeof reply.createdAt.toDate === 'function') {
                                    // Firestore Timestamp
                                    createdDate = reply.createdAt.toDate();
                                  } else {
                                    // Fallback: try to convert to Date
                                    createdDate = new Date(reply.createdAt);
                                  }
                                  return (
                                    <span>
                                      {createdDate.toLocaleDateString('en-GB')} at {createdDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  );
                                })()}
                              </div>
                              
                              {/* Reply Photos */}
                              {reply.photoUrls && reply.photoUrls.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                                  <div style={{ flex: 1 }}>
                                    {renderMedia(reply.photoUrls, 'photo', reply, true)}
                                  </div>
                                  {!isReadOnly && (
                                  <button
                                    onClick={() => setPmReplyModal({ open: true, replyId: reply.id, snagId: snag.id })}
                                    style={{
                                      background: 'var(--branding-app-primary)',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: 8,
                                      padding: '12px 24px',
                                      fontSize: 14,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      minWidth: 100,
                                      height: 'fit-content',
                                      alignSelf: 'flex-start'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = 'var(--branding-app-primary-hover)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'var(--branding-app-primary)';
                                    }}
                                  >
                                    Reply
                                  </button>
                                  )}
                                </div>
                              )}

                              {/* Reply Voice Note */}
                              {reply.voiceNoteUrl && (
                                <div style={{ marginTop: 12 }}>
                                  {renderMedia([reply.voiceNoteUrl], 'voice', reply)}
                                </div>
                              )}
                              
                              {/* Reply Text Comment */}
                              {reply.comment && reply.comment.trim() && (
                                <div style={{ marginTop: 12 }}>
                                  <div style={{
                                    fontSize: 13,
                                    color: 'var(--cc-text-primary)',
                                    lineHeight: 1.4,
                                    padding: '8px 12px',
                                    background: 'var(--branding-app-surface-panel)',
                                    borderRadius: 6,
                                    border: '1px solid var(--branding-app-surface-panel-border)'
                                  }}>
                                    {reply.comment}
                                  </div>
                                </div>
                              )}
                              
                              {/* PM Reply Button for replies without photos */}
                              {!isReadOnly && (!reply.photoUrls || reply.photoUrls.length === 0) && (
                                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                                  <button
                                    onClick={() => setPmReplyModal({ open: true, replyId: reply.id, snagId: snag.id })}
                                    style={{
                                      background: 'var(--branding-app-primary)',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: 8,
                                      padding: '12px 24px',
                                      fontSize: 14,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      minWidth: 100
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = 'var(--branding-app-primary-hover)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'var(--branding-app-primary)';
                                    }}
                                  >
                                    Reply
                                  </button>
                                </div>
                              )}

                              {/* PM Replies */}
                              {pmRepliesByReply[reply.id] && pmRepliesByReply[reply.id].length > 0 && (
                                <div style={{ 
                                  marginTop: 12, 
                                  paddingLeft: 16,
                                  borderLeft: '2px solid #e2e8f0'
                                }}>
                                  {pmRepliesByReply[reply.id].map((pmReply) => (
                                    <div key={pmReply.id} style={{
                                      background: 'var(--branding-app-surface-panel)',
                                      borderRadius: 6,
                                      padding: 10,
                                      marginBottom: 8,
                                      border: '1px solid var(--branding-app-surface-panel-border)'
                                    }}>
                                      <div style={{ 
                                        fontSize: 12, 
                                        color: 'var(--branding-app-surface-panel-hint)', 
                                        marginBottom: 4,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}>
                                        <span style={{ fontWeight: 500, color: 'var(--branding-app-primary)' }}>
                                          {pmReply.createdByName}
                                        </span>
                                        {pmReply.createdAt && (() => {
                                          // Ensure createdAt is a Date object (handle Date, Timestamp, or other formats)
                                          let createdDate;
                                          if (pmReply.createdAt instanceof Date) {
                                            createdDate = pmReply.createdAt;
                                          } else if (pmReply.createdAt?.toDate && typeof pmReply.createdAt.toDate === 'function') {
                                            // Firestore Timestamp
                                            createdDate = pmReply.createdAt.toDate();
                                          } else {
                                            // Fallback: try to convert to Date
                                            createdDate = new Date(pmReply.createdAt);
                                          }
                                          return (
                                            <span>
                                              {createdDate.toLocaleDateString('en-GB')} at {createdDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                          );
                                        })()}
                                      </div>
                                      
                                      {/* Comment Text */}
                                      {pmReply.comment && (
                                        <div style={{
                                          fontSize: 13,
                                          color: 'var(--cc-text-primary)',
                                          lineHeight: 1.4,
                                          marginBottom: pmReply.photoUrls || pmReply.voiceNoteUrl ? 8 : 0
                                        }}>
                                          {pmReply.comment}
                                        </div>
                                      )}
                                      
                                      {/* PM Reply Images */}
                                      {pmReply.photoUrls && pmReply.photoUrls.length > 0 && (
                                        <div style={{ marginTop: pmReply.comment ? 8 : 0 }}>
                                          {renderMedia(pmReply.photoUrls, 'photo', pmReply, true)}
                                        </div>
                                      )}
                                      
                                      {/* PM Reply Voice Note */}
                                      {pmReply.voiceNoteUrl && (
                                        <div style={{ marginTop: (pmReply.comment || pmReply.photoUrls) ? 8 : 0 }}>
                                          {renderMedia([pmReply.voiceNoteUrl], 'voice', pmReply)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                             </div>
                           ))}
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               );
             })}
          </div>
        )}
      </div>

      {/* Snag Creation Modal */}
      <SnagModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateSnag}
        projectId={projectId}
        teams={teams}
      />
      
      {/* Image Modal */}
      <ImageModal
        open={imageModal.open}
        onClose={() => setImageModal({ open: false, url: null, reply: null, initialIndex: 0 })}
        imageUrl={imageModal.url}
        reply={imageModal.reply}
        initialIndex={imageModal.initialIndex}
      />
      
      {/* PM Reply Modal */}
      <PmReplyModal
        open={pmReplyModal.open}
        onClose={() => setPmReplyModal({ open: false, replyId: null, snagId: null })}
        replyId={pmReplyModal.replyId}
        snagId={pmReplyModal.snagId}
      />
      
      {/* Close Confirmation Modal */}
      <CloseConfirmModal
        open={closeConfirmModal.open}
        onClose={() => setCloseConfirmModal({ open: false, snagId: null, snagTitle: null })}
        snagId={closeConfirmModal.snagId}
        snagTitle={closeConfirmModal.snagTitle}
        onConfirm={() => handleCloseSnag(closeConfirmModal.snagId)}
      />

      <PdfExportSettingsModal
        open={pdfExportSettingsOpen}
        onClose={() => setPdfExportSettingsOpen(false)}
        variant="snags"
        value={snagsPdfSettings}
        onChange={setSnagsPdfSettings}
        companyLogoAvailable={Boolean(companyLogoUrl)}
      />

      <ReportSectionModal
        open={sectionModalOpen}
        onClose={() => { setSectionModalOpen(false); setEditingSection(null); }}
        onSave={handleSaveSection}
        selectedCount={editingSection ? 0 : exportSelectedIds.size}
        existingSection={editingSection}
      />
    </div>
  );
}

export default withProjectPermissions(SnagsLanding, { requireEdit: true });